import React, { useEffect, useRef, useState } from 'react';
import { Info, RotateCcw, SendHorizonal, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v4 as uuidv4 } from 'uuid';
import { AI_SERVICE_CONFIG } from '@/config/aiConfig';
import { createSession, streamMessage, type DashboardContext } from '@/services/aiChatService';
import teamscxLogo from '@/assets/images/teamscx.png';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  responseType?: string;
  status?: string;
}

interface ChatWidgetProps {
  /** Bearer token forwarded to the AI service (and onward to the ledger API). */
  bearerToken: string;
  locale?: string;
  timezone?: string;
  dashboardContext?: DashboardContext;
}

function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function getInitialAssistantMessage(): string {
  return `${getTimeBasedGreeting()}, I'm TeamsCX AI agent, I am happy to help you.`;
}

const MESSAGE_MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <p className="leading-8">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#d8e6f7]">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-slate-200">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3 py-1.5 text-left font-semibold text-[#15345d]">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-1.5 text-[#15345d]">{children}</td>,
};

function BrandMark({ compact = false }: Readonly<{ compact?: boolean }>) {
  return (
    <div
      className={compact
        ? 'h-8 w-8 shrink-0 rounded-full overflow-hidden bg-[#0f3657]'
        : 'h-14 w-14 rounded-full overflow-hidden bg-[#0f3657] shadow-[0_10px_22px_rgba(15,54,87,0.28)]'}
    >
      <img src={teamscxLogo} alt="TeamsCX" className="h-full w-full object-cover" />
    </div>
  );
}

const BUBBLE_SIZE = 48;
const PANEL_GAP = 12;
const MIN_W = 280, MAX_W = 680, MIN_H = 360, MAX_H = 900;

function computePanelPos(bubbleX: number, bubbleY: number, w: number, h: number) {
  const left = bubbleX + BUBBLE_SIZE - w;
  let top = bubbleY - h - PANEL_GAP;
  // If panel would clip the top, open below the bubble instead
  if (top < 8) top = bubbleY + BUBBLE_SIZE + PANEL_GAP;
  return {
    left: Math.max(8, Math.min(window.innerWidth - w - 8, left)),
    top: Math.max(8, Math.min(window.innerHeight - h - 8, top)),
  };
}

export default function ChatWidget({ bearerToken, locale = 'en', timezone = 'UTC', dashboardContext }: Readonly<ChatWidgetProps>) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: 'assistant-welcome', role: 'assistant', text: getInitialAssistantMessage() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => uuidv4());
  const [sessionReady, setSessionReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Draggable bubble — top-left corner in viewport pixels
  const [bubblePos, setBubblePos] = useState(() => ({
    x: window.innerWidth - BUBBLE_SIZE - 28,   // right-7 = 28 px
    y: window.innerHeight - BUBBLE_SIZE - 32,  // bottom-8 = 32 px
  }));
  const hasDragged = useRef(false);

  // Panel position (top-left) and size
  const [panelPos, setPanelPos] = useState({ left: 0, top: 0 });
  const [chatSize, setChatSize] = useState({ width: 344, height: 480 });

  // Keep bubble inside viewport on window resize
  useEffect(() => {
    const handler = () =>
      setBubblePos(p => ({
        x: Math.min(p.x, window.innerWidth - BUBBLE_SIZE),
        y: Math.min(p.y, window.innerHeight - BUBBLE_SIZE),
      }));
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Session init
  useEffect(() => {
    if (!open || sessionReady || !AI_SERVICE_CONFIG.enabled) return;
    createSession(sessionId, locale, timezone, bearerToken, dashboardContext)
      .then(() => setSessionReady(true))
      .catch(() => setError('Could not connect to AI service.'));
  }, [open, sessionReady, sessionId, locale, timezone, bearerToken, dashboardContext]);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Bubble drag ──────────────────────────────────────────────────────────────
  function onBubbleMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    hasDragged.current = false;
    const startX = e.clientX, startY = e.clientY;
    const initX = bubblePos.x, initY = bubblePos.y;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) hasDragged.current = true;
      setBubblePos({
        x: Math.max(0, Math.min(window.innerWidth - BUBBLE_SIZE, initX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - BUBBLE_SIZE, initY + dy)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function onBubbleClick() {
    if (hasDragged.current) return;
    const nextOpen = !open;
    if (nextOpen) {
      setPanelPos(computePanelPos(bubblePos.x, bubblePos.y, chatSize.width, chatSize.height));
    }
    setOpen(nextOpen);
  }

  // ── Panel header drag (move panel) ───────────────────────────────────────────
  function onHeaderMouseDown(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const initLeft = panelPos.left, initTop = panelPos.top;

    const onMove = (ev: MouseEvent) => {
      setPanelPos({
        left: Math.max(0, Math.min(window.innerWidth - chatSize.width, initLeft + ev.clientX - startX)),
        top: Math.max(0, Math.min(window.innerHeight - chatSize.height, initTop + ev.clientY - startY)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // ── Panel resize (drag bottom-right corner) ───────────────────────────────────
  function onResizeMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const initW = chatSize.width, initH = chatSize.height;

    const onMove = (ev: MouseEvent) => {
      setChatSize({
        width: Math.max(MIN_W, Math.min(MAX_W, initW + (ev.clientX - startX))),
        height: Math.max(MIN_H, Math.min(MAX_H, initH + (ev.clientY - startY))),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // ── Message handling ─────────────────────────────────────────────────────────
  async function handleSend() {
    const question = input.trim();
    if (!question || loading || !sessionReady) return;
    setInput('');
    setMessages(prev => [...prev, { id: uuidv4(), role: 'user', text: question }]);
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;

    const assistantId = uuidv4();
    let streaming = false;

    try {
      for await (const frame of streamMessage(sessionId, question, bearerToken, dashboardContext, controller.signal)) {
        if (controller.signal.aborted) return;

        if (frame.type === 'delta') {
          if (!streaming) {
            streaming = true;
            setLoading(false);
            setMessages(prev => [...prev, { id: assistantId, role: 'assistant', text: frame.content }]);
          } else {
            setMessages(prev => prev.map(m =>
              m.id === assistantId ? { ...m, text: m.text + frame.content } : m,
            ));
          }
        } else if (frame.type === 'done') {
          if (streaming) {
            setMessages(prev => prev.map(m =>
              m.id === assistantId ? { ...m, text: frame.answer, status: frame.status } : m,
            ));
          } else {
            setLoading(false);
            setMessages(prev => [...prev, { id: assistantId, role: 'assistant', text: frame.answer, status: frame.status }]);
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError('Failed to get a response. Please try again.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  function handleReset() {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setMessages([{ id: 'assistant-welcome', role: 'assistant', text: getInitialAssistantMessage() }]);
    setInput('');
    setError(null);
    setSessionReady(false);
    setSessionId(uuidv4());
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  if (!AI_SERVICE_CONFIG.enabled) return null;

  return (
    <>
      {/* ── Draggable chat bubble ──────────────────────────────────────────── */}
      <button
        aria-label={open ? 'Close AI chat' : 'Open AI chat'}
        onMouseDown={onBubbleMouseDown}
        onClick={onBubbleClick}
        style={{ left: bubblePos.x, top: bubblePos.y }}
        className="fixed z-50 h-[48px] w-[48px] rounded-full overflow-hidden bg-[#0f3657] text-white shadow-[0_12px_28px_rgba(15,54,87,0.24)] hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#6f95bd] cursor-grab active:cursor-grabbing select-none transition-transform duration-200"
      >
        {open
          ? <div className="flex h-full w-full items-center justify-center"><X className="h-4 w-4" strokeWidth={2.25} /></div>
          : <img src={teamscxLogo} alt="TeamsCX" className="h-full w-full object-cover" />}
      </button>

      {/* ── Resizable + draggable chat panel ──────────────────────────────── */}
      {open && (
        <div
          style={{ left: panelPos.left, top: panelPos.top, width: chatSize.width, height: chatSize.height }}
          className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,54,87,0.18)]"
        >
          {/* Header — drag to move panel */}
          <div
            onMouseDown={onHeaderMouseDown}
            className="flex cursor-move items-center justify-between border-b border-slate-100 px-5 py-4 select-none"
          >
            <div className="flex items-center gap-3">
              <BrandMark compact />
              <span className="text-[14px] font-semibold text-[#0f2f56]">AI Agent</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                aria-label="Reset chat"
                className="cursor-pointer rounded-full p-1 text-[#0f2f56] transition-colors hover:bg-slate-100"
              >
                <RotateCcw className="h-4 w-4" strokeWidth={2.25} />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="cursor-pointer rounded-full p-1 text-[#0f2f56] transition-colors hover:bg-slate-100"
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
          </div>

          {/* Info bar */}
          <div className="flex items-start gap-3 bg-[#eef4ff] px-5 py-3 text-[12px] leading-4 text-[#365cf5]">
            <Info className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.2} />
            <p>This conversation is process of AI Agent, support multi languages.</p>
          </div>

          {/* Messages — flex-1 + min-h-0 fills remaining panel height */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-end gap-2'}`}
              >
                {msg.role === 'assistant' && <BrandMark compact />}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 text-[12px] leading-8 ${msg.role === 'user' ? 'bg-[#0f3657] text-white' : 'bg-[#eef2f7] text-[#15345d]'
                    }`}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MESSAGE_MARKDOWN_COMPONENTS}>
                    {msg.text}
                  </ReactMarkdown>
                  {msg.status === 'fallback' && (
                    <span className="mt-2 block text-[11px] opacity-60">(fallback response)</span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-end gap-2">
                <BrandMark compact />
                <div className="rounded-2xl bg-[#eef2f7] px-4 py-3 text-[12px] text-slate-500">Thinking...</div>
              </div>
            )}
            {error && <p className="text-center text-[11px] text-red-500">{error}</p>}
            <div ref={bottomRef} />
          </div>

          {/* Footer disclaimer */}
          <div className="border-t border-slate-200 bg-[#eef4ff] px-5 py-2 text-[11px] leading-5 text-[#365cf5]">
            <p>Always ensure you are in a secure and private network.</p>
            <p>CX AI Agent is AI and can make mistakes.</p>
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-5 py-4">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={sessionReady ? 'Type your message...' : 'Connecting...'}
              disabled={!sessionReady || loading}
              className="h-[40px] flex-1 rounded-md border border-slate-200 px-4 text-[14px] text-slate-700 outline-none transition focus:border-[#8ca7c7] focus:ring-2 focus:ring-[#d8e6f7] disabled:bg-slate-50 disabled:text-slate-400"
            />
            <button
              onClick={() => void handleSend()}
              disabled={!input.trim() || !sessionReady || loading}
              className="flex h-[40px] w-[42px] items-center justify-center rounded-md bg-[#9eb1c6] text-white transition hover:bg-[#829ab4] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SendHorizonal className="h-4 w-4" strokeWidth={2.2} />
            </button>
          </div>

          {/* Bottom-right resize handle */}
          <div
            onMouseDown={onResizeMouseDown}
            title="Drag to resize"
            className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
            style={{
              background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%, #94a3b8 60%, transparent 60%, transparent 70%, #94a3b8 70%, #94a3b8 80%, transparent 80%)',
              borderRadius: '0 0 12px 0',
            }}
          />
        </div>
      )}
    </>
  );
}
